/* eslint-disable max-lines */
import {
    IconGoogleAnalytics,
    IconLojaIntegrada,
    IconPareto,
    IconPipedrive,
    IconWoocommerce,
    IconFacebook2,
    IconVtex,
} from '@paretogroup/tess';
import { BigNumbersConfigData } from '@domain/user-config/user-config.types';
import { PlatformMetricsObject } from './big-numbers.types';

export const TEST_ID = {
    OVERVIEW_WIDGET: {
        BOX: 'big-numbers-widget-box',
        VARIATION_PERCENTAGE: 'big-numbers-widget-variation-percentage',
        COMPARISON_TOOLTIP: 'big-numbers-widget-comparison-tooltip',
        SELECTOR: 'big-numbers-widget-selector',
        CONVERSION_TYPE: 'big-numbers-widget-conversion-type',
        DIV_TOOLTIP: 'big-numbers-widget-div-tooltip',
        METRICS_SELECTOR: 'big-numbers-widget-metrics-selector',
    },
};

export const APPCUES_ID = {
    BIG_NUMBERS_WIDGET_INDEX: 'BIG_NUMBERS_WIDGET_INDEX',
    BIG_NUMBERS_WIDGET_COMPARATIVE: 'BIG_NUMBERS_WIDGET_COMPARATIVE',
};

const GOAL_COMPLETION = 'goalCompletion';
const TRANSACTIONS = 'transactions';
const TRANSACTIONS_REVENUE = 'transactionsRevenue';
const DEAL_WON = 'deal_won';
const DEAL_WON_VALUE = 'deal_won_value';
const PIPEDRIVE_CPA = 'pipedrive_cpa';
const PIPEDRIVE_ROI = 'pipedrive_roi';
const COST_PER_GOAL = 'cost_per_goal';
const COST_PER_CONVERSION = 'cost_per_conversion';
const COST_PER_TRANSACTION = 'cost_per_transaction';
const COST = 'cost';
const IMPRESSIONS = 'impressions';
const CLICKS = 'clicks';
const SESSIONS = 'sessions';
const USERS = 'users';
const NEW_USERS = 'newUsers';
const PAGE_VIEWS = 'pageViews';
const CPC = 'cpc';
const CPM = 'cpm';
const CTR = 'ctr';
const ROI_GA = 'roi_ga';
const ROAS = 'roas';
const GOALS_SESSION = 'goals_session';
const TRANSACTION_SESSIONS = 'transactions_session';
const REACH = 'reach';
const VIDEO_VIEWS = 'videoviews';
const CONV_RATE = 'conv_rate';
const CONVERSIONS = 'conversions';
const REVENUE = 'revenue';
const LOJA_INTEGRADA_AVERAGE_TICKET = 'average_ticket';
const LOJA_INTEGRADA_ORDER_QUANTITY = 'order_quantity';
const LOJA_INTEGRADA_TOTAL_VALUE = 'total_value';
const WOOCOMMERCE_TOTAL_VALUE = 'woocommerce_total_value';
const WOOCOMMERCE_ORDER_QUANTITY = 'woocommerce_order_quantity';
const WOOCOMMERCE_AVERAGE_TICKET = 'woocommerce_average_ticket';
const PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR = 'pareto_ads_company_economy_man_hour';
const PARETO_ADS_MY_ECONOMY_MAN_HOUR = 'pareto_ads_my_economy_man_hour';
const VTEX_REVENUE = 'vtex_revenue';
const VTEX_TRANSACTIONS = 'vtex_transactions';

export const widgetConstants = {
    metrics: {
        SESSION: 'Session',
        PAGE_VIEWS: 'Page Views',
        USERS: 'Users',
        NEW_USERS: 'New Users',
        CONVERSIONS_GA: 'Goal Completion',
        CONVERSION_TRANSACTION: 'Transactions',
        CONVERSION_REVENUE: 'Revenue',
        CONVERSIONS: 'Conversions',
        CONV_VALUE: 'Conversions Value',
        COST_PER_GOAL: 'Cost per Goal',
        COST_PER_TRANSACTION: 'Cost per Transaction',
        COST_PER_CONVERSION: 'Cost per Conversion',
        COST_PER_REVENUE: 'Cost per Revenue',
        ROI_GA: 'ROI GA',
        ROI: 'ROI',
        GOALS_SESSION: 'Goals / Session',
        TRANSACTIONS_SESSION: 'Transactions / Session',
        INVESTMENT: 'Investment',
        IMPRESSIONS: 'Impressions',
        REACH: 'Reach',
        CLICKS: 'Clicks',
        CPC: 'CPC',
        CTR: 'CTR',
        CPM: 'CPM',
        CPA: 'CPA',
        CONV_RATE: 'Conv. Rate',
        ROAS: 'ROAS',
        DEALS_WON: 'Deals Won',
        DEALS_WON_VALUE: 'Deals Won Value',
        PIPEDRIVE_CPA: 'Pipedrive CPA',
        PIPEDRIVE_ROI: 'Pipedrive ROI',
        VIDEO_VIEWS: 'Video Views',
        LOJA_INTEGRADA_AVERAGE_TICKET: 'Average Ticket',
        LOJA_INTEGRADA_TOTAL_VALUE: 'Total Value',
        LOJA_INTEGRADA_ORDER_QUANTITY: 'Order Quantity',
        WOOCOMMERCE_AVERAGE_TICKET: 'Average Ticket',
        WOOCOMMERCE_TOTAL_VALUE: 'Total Value',
        WOOCOMMERCE_ORDER_QUANTITY: 'Order Quantity',
        PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR: 'Company Saved Hours with Pareto',
        PARETO_ADS_MY_ECONOMY_MAN_HOUR: 'My Saved Hours with Pareto',
        VTEX_TRANSACTIONS: 'Approved Transactions',
        VTEX_REVENUE: 'Approved Revenue',
    },
    metricOrigin: {
        GOOGLE_FACEBOOK: 'From Google and Facebook Ads',
        GA: 'From Google Analytics',
        PIPEDRIVE: 'From Pipedrive',
        LOJA_INTEGRADA: 'From Loja Integrada',
        WOOCOMMERCE: 'From Loja Woocommerce',
        PARETO_ADS: 'From Pareto Ads',
        FACEBOOK_CONVERSION_TYPES: 'From Facebook Conversion Types',
        VTEX: 'From VTEX',
    },
};

const ALL_METRICS_OPTIONS: PlatformMetricsObject = {
    [CONVERSIONS]: {
        value: CONVERSIONS,
        label: widgetConstants.metrics.CONVERSIONS,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [COST_PER_GOAL]: {
        value: COST_PER_GOAL,
        label: widgetConstants.metrics.COST_PER_GOAL,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [COST]: {
        value: COST,
        label: widgetConstants.metrics.INVESTMENT,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [IMPRESSIONS]: {
        value: IMPRESSIONS,
        label: widgetConstants.metrics.IMPRESSIONS,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [CLICKS]: {
        value: CLICKS,
        label: widgetConstants.metrics.CLICKS,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [SESSIONS]: {
        value: SESSIONS,
        label: widgetConstants.metrics.SESSION,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [CPC]: {
        value: CPC,
        label: widgetConstants.metrics.CPC,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [CPM]: {
        value: CPM,
        label: widgetConstants.metrics.CPM,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [CTR]: {
        value: CTR,
        label: widgetConstants.metrics.CTR,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [CONV_RATE]: {
        value: CONV_RATE,
        label: widgetConstants.metrics.CONV_RATE,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [COST_PER_CONVERSION]: {
        value: COST_PER_CONVERSION,
        label: widgetConstants.metrics.CPA,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [REACH]: {
        value: REACH,
        label: widgetConstants.metrics.REACH,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [VIDEO_VIEWS]: {
        value: VIDEO_VIEWS,
        label: widgetConstants.metrics.VIDEO_VIEWS,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [REVENUE]: {
        value: REVENUE,
        label: widgetConstants.metrics.CONV_VALUE,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [GOAL_COMPLETION]: {
        value: GOAL_COMPLETION,
        label: widgetConstants.metrics.CONVERSIONS_GA,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [TRANSACTIONS]: {
        value: TRANSACTIONS,
        label: widgetConstants.metrics.CONVERSION_TRANSACTION,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [TRANSACTIONS_REVENUE]: {
        value: TRANSACTIONS_REVENUE,
        label: widgetConstants.metrics.CONVERSION_REVENUE,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [DEAL_WON]: {
        value: DEAL_WON,
        label: widgetConstants.metrics.DEALS_WON,
        metricOrigin: widgetConstants.metricOrigin.PIPEDRIVE,
    },
    [DEAL_WON_VALUE]: {
        value: DEAL_WON_VALUE,
        label: widgetConstants.metrics.DEALS_WON_VALUE,
        metricOrigin: widgetConstants.metricOrigin.PIPEDRIVE,
    },
    [PIPEDRIVE_CPA]: {
        value: PIPEDRIVE_CPA,
        label: widgetConstants.metrics.PIPEDRIVE_CPA,
        metricOrigin: widgetConstants.metricOrigin.PIPEDRIVE,
    },
    [PIPEDRIVE_ROI]: {
        value: PIPEDRIVE_ROI,
        label: widgetConstants.metrics.PIPEDRIVE_ROI,
        metricOrigin: widgetConstants.metricOrigin.PIPEDRIVE,
    },
    [COST_PER_CONVERSION]: {
        value: COST_PER_CONVERSION,
        isInverseMetric: true,
        label: widgetConstants.metrics.CPA,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [COST_PER_TRANSACTION]: {
        value: COST_PER_TRANSACTION,
        label: widgetConstants.metrics.COST_PER_TRANSACTION,
        isInverseMetric: true,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [USERS]: {
        value: USERS,
        label: widgetConstants.metrics.USERS,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [NEW_USERS]: {
        value: NEW_USERS,
        label: widgetConstants.metrics.NEW_USERS,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [PAGE_VIEWS]: {
        value: PAGE_VIEWS,
        label: widgetConstants.metrics.PAGE_VIEWS,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [ROI_GA]: {
        value: ROI_GA,
        label: widgetConstants.metrics.ROI_GA,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [ROAS]: {
        value: ROAS,
        label: widgetConstants.metrics.ROI,
        metricOrigin: widgetConstants.metricOrigin.GOOGLE_FACEBOOK,
    },
    [GOALS_SESSION]: {
        value: GOALS_SESSION,
        label: widgetConstants.metrics.GOALS_SESSION,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [TRANSACTION_SESSIONS]: {
        value: TRANSACTION_SESSIONS,
        label: widgetConstants.metrics.TRANSACTIONS_SESSION,
        metricOrigin: widgetConstants.metricOrigin.GA,
    },
    [LOJA_INTEGRADA_AVERAGE_TICKET]: {
        value: LOJA_INTEGRADA_AVERAGE_TICKET,
        label: widgetConstants.metrics.LOJA_INTEGRADA_AVERAGE_TICKET,
        metricOrigin: widgetConstants.metricOrigin.LOJA_INTEGRADA,
    },
    [LOJA_INTEGRADA_ORDER_QUANTITY]: {
        value: LOJA_INTEGRADA_ORDER_QUANTITY,
        label: widgetConstants.metrics.LOJA_INTEGRADA_ORDER_QUANTITY,
        metricOrigin: widgetConstants.metricOrigin.LOJA_INTEGRADA,
    },
    [LOJA_INTEGRADA_TOTAL_VALUE]: {
        value: LOJA_INTEGRADA_TOTAL_VALUE,
        label: widgetConstants.metrics.LOJA_INTEGRADA_TOTAL_VALUE,
        metricOrigin: widgetConstants.metricOrigin.LOJA_INTEGRADA,
    },
    [WOOCOMMERCE_TOTAL_VALUE]: {
        value: WOOCOMMERCE_TOTAL_VALUE,
        label: widgetConstants.metrics.WOOCOMMERCE_TOTAL_VALUE,
        metricOrigin: widgetConstants.metricOrigin.WOOCOMMERCE,
    },
    [WOOCOMMERCE_ORDER_QUANTITY]: {
        value: WOOCOMMERCE_ORDER_QUANTITY,
        label: widgetConstants.metrics.WOOCOMMERCE_ORDER_QUANTITY,
        metricOrigin: widgetConstants.metricOrigin.WOOCOMMERCE,
    },
    [WOOCOMMERCE_AVERAGE_TICKET]: {
        value: WOOCOMMERCE_AVERAGE_TICKET,
        label: widgetConstants.metrics.WOOCOMMERCE_AVERAGE_TICKET,
        metricOrigin: widgetConstants.metricOrigin.WOOCOMMERCE,
    },
    [PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR]: {
        value: PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR,
        label: widgetConstants.metrics.PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR,
        metricOrigin: widgetConstants.metricOrigin.PARETO_ADS,
    },
    [PARETO_ADS_MY_ECONOMY_MAN_HOUR]: {
        value: PARETO_ADS_MY_ECONOMY_MAN_HOUR,
        label: widgetConstants.metrics.PARETO_ADS_MY_ECONOMY_MAN_HOUR,
        metricOrigin: widgetConstants.metricOrigin.PARETO_ADS,
    },
    [VTEX_REVENUE]: {
        value: VTEX_REVENUE,
        label: widgetConstants.metrics.VTEX_REVENUE,
        metricOrigin: widgetConstants.metricOrigin.VTEX,
    },
    [VTEX_TRANSACTIONS]: {
        value: VTEX_TRANSACTIONS,
        label: widgetConstants.metrics.VTEX_TRANSACTIONS,
        metricOrigin: widgetConstants.metricOrigin.VTEX,
    },
};

export const platformsOptions: BigNumbersConfigData = {
    google_ads: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[REVENUE],
        ALL_METRICS_OPTIONS[COST_PER_CONVERSION],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[VIDEO_VIEWS],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
        ALL_METRICS_OPTIONS[ROAS],
    ],
    facebook_ads: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[REVENUE],
        ALL_METRICS_OPTIONS[COST_PER_CONVERSION],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[REACH],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
        ALL_METRICS_OPTIONS[ROAS],
    ],
    client: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[REVENUE],
        ALL_METRICS_OPTIONS[GOAL_COMPLETION],
        ALL_METRICS_OPTIONS[TRANSACTIONS],
        ALL_METRICS_OPTIONS[TRANSACTIONS_REVENUE],
        ALL_METRICS_OPTIONS[DEAL_WON],
        ALL_METRICS_OPTIONS[DEAL_WON_VALUE],
        ALL_METRICS_OPTIONS[PIPEDRIVE_CPA],
        ALL_METRICS_OPTIONS[PIPEDRIVE_ROI],
        ALL_METRICS_OPTIONS[COST_PER_GOAL],
        ALL_METRICS_OPTIONS[COST_PER_CONVERSION],
        ALL_METRICS_OPTIONS[COST_PER_TRANSACTION],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[SESSIONS],
        ALL_METRICS_OPTIONS[USERS],
        ALL_METRICS_OPTIONS[NEW_USERS],
        ALL_METRICS_OPTIONS[PAGE_VIEWS],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
        ALL_METRICS_OPTIONS[ROI_GA],
        ALL_METRICS_OPTIONS[ROAS],
        ALL_METRICS_OPTIONS[GOALS_SESSION],
        ALL_METRICS_OPTIONS[TRANSACTION_SESSIONS],
        ALL_METRICS_OPTIONS[LOJA_INTEGRADA_AVERAGE_TICKET],
        ALL_METRICS_OPTIONS[LOJA_INTEGRADA_ORDER_QUANTITY],
        ALL_METRICS_OPTIONS[LOJA_INTEGRADA_TOTAL_VALUE],
        ALL_METRICS_OPTIONS[WOOCOMMERCE_TOTAL_VALUE],
        ALL_METRICS_OPTIONS[WOOCOMMERCE_AVERAGE_TICKET],
        ALL_METRICS_OPTIONS[PARETO_ADS_COMPANY_ECONOMY_MAN_HOUR],
        ALL_METRICS_OPTIONS[PARETO_ADS_MY_ECONOMY_MAN_HOUR],
        ALL_METRICS_OPTIONS[VTEX_REVENUE],
        ALL_METRICS_OPTIONS[VTEX_TRANSACTIONS],
    ],
};

export const DEFAULT_BIG_NUMBER_CONFIG: BigNumbersConfigData = {
    client: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[COST_PER_GOAL],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[SESSIONS],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
    ],
    facebook_ads: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[COST_PER_CONVERSION],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[REACH],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
    ],
    google_ads: [
        ALL_METRICS_OPTIONS[CONVERSIONS],
        ALL_METRICS_OPTIONS[COST_PER_CONVERSION],
        ALL_METRICS_OPTIONS[COST],
        ALL_METRICS_OPTIONS[IMPRESSIONS],
        ALL_METRICS_OPTIONS[CLICKS],
        ALL_METRICS_OPTIONS[VIDEO_VIEWS],
        ALL_METRICS_OPTIONS[CPC],
        ALL_METRICS_OPTIONS[CPM],
        ALL_METRICS_OPTIONS[CTR],
        ALL_METRICS_OPTIONS[CONV_RATE],
    ],
};

export const METRIC_ORIGIN_ICONS = {
    [widgetConstants.metricOrigin.GA]: IconGoogleAnalytics,
    [widgetConstants.metricOrigin.LOJA_INTEGRADA]: IconLojaIntegrada,
    [widgetConstants.metricOrigin.PARETO_ADS]: IconPareto,
    [widgetConstants.metricOrigin.PIPEDRIVE]: IconPipedrive,
    [widgetConstants.metricOrigin.WOOCOMMERCE]: IconWoocommerce,
    [widgetConstants.metricOrigin.FACEBOOK_CONVERSION_TYPES]: IconFacebook2,
    [widgetConstants.metricOrigin.VTEX]: IconVtex,
};
